import { TextButton } from '@creditornot/cb-button';
import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import useMenuData from 'modules/menu/useMenuData';

import { BulkWoltSpecialErrorCode } from './utils';

type Props = {
  itemErrorsMap: {
    [itemId: string]: {
      code: BulkWoltSpecialErrorCode;
      message: 'string';
    }[];
  };
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-height: 180px;
`;

const ShowDetailsButton = styled(TextButton)`
  margin-block-start: 8px;
`;

const ErrorList = styled.div`
  width: 335px;
  margin-block-start: 12px;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
`;

const ItemErrors = styled.div`
  display: flex;
  flex-direction: column;
  margin-block-end: 4px;
`;

const ItemName = styled(T.Body3)`
  font-weight: bold;
`;

const errorCodesToPhraseKeys: { [key in BulkWoltSpecialErrorCode]: string } = {
  DISCOUNT_AMOUNT_EXCEEDS_ORIGINAL_PRICE: 'menu.bulkActions.createWoltSpecials.errors.invalidDiscount',
  ITEM_IS_ALREADY_WOLT_SPECIALS: 'menu.bulkActions.createWoltSpecials.errors.cannotCreateFromSpecial',
};

const DetailedErrorList = ({ itemErrorsMap }: Props) => {
  const [showDetails, setShowDetails] = useState(false);
  const { items } = useMenuData();

  const { primaryLanguage } = useContext(MenuLocaleContext);

  return (
    <Root>
      <FormattedMessage id="menu.bulkActions.createWoltSpecials.detailedErrorDescription" />
      <ShowDetailsButton onClick={() => setShowDetails(!showDetails)}>
        <FormattedMessage id={showDetails ? 'errors.hideErrorDetails' : 'errors.showErrorDetails'} />
      </ShowDetailsButton>
      {showDetails && (
        <ErrorList>
          {Object.entries(itemErrorsMap).map(itemErrors => {
            const [itemId, errors] = itemErrors;
            const item = items.find(item => item.id === itemId);

            return (
              <ItemErrors key={itemId}>
                <ItemName>{item ? localize(item.name, primaryLanguage) : itemId}:</ItemName>
                {errors.map(error => (
                  <T.Body3 key={error.code}>
                    • <FormattedMessage id={errorCodesToPhraseKeys[error.code]} />
                  </T.Body3>
                ))}
              </ItemErrors>
            );
          })}
        </ErrorList>
      )}
    </Root>
  );
};

export default DetailedErrorList;
